/**
 * Cookie Policy Page
 *
 * Information about cookies used on the platform.
 */

import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSEO, useHreflang, SITE_CONFIG } from '../../lib/seo';
import PublicLayout from '../../components/layout/PublicLayout';

export default function CookiePolicyPage() {
  const { i18n } = useTranslation();

  useSEO({
    title: 'Cookie Policy | xtFORM',
    description: 'Learn about how xtFORM uses cookies and similar technologies to improve your experience and provide our services.',
    keywords: 'cookie policy, cookies, privacy, xtFORM cookies',
    url: `${SITE_CONFIG.domain}/cookies`,
    locale: i18n.language,
  });

  useHreflang('/cookies');

  return (
    <PublicLayout bgClass="bg-gray-50">
      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <article className="bg-white rounded-xl shadow-sm p-8 md:p-12">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Cookie Policy</h1>
          <p className="text-gray-500 mb-8">Last updated: December 2024</p>

          <div className="prose prose-lg max-w-none prose-headings:font-bold prose-headings:text-gray-900">
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">What Are Cookies?</h2>
              <p className="text-gray-600 mb-4">
                Cookies are small text files that are stored on your device (computer, tablet, or mobile) when you visit a website.
                They help the website remember your preferences and provide a better user experience.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">How We Use Cookies</h2>
              <p className="text-gray-600 mb-4">
                xtFORM uses cookies and similar technologies for the following purposes:
              </p>

              <h3 className="text-lg font-semibold text-gray-900 mt-6 mb-3">Essential Cookies</h3>
              <p className="text-gray-600 mb-4">
                These cookies are necessary for the website to function properly. They enable core functionality such as:
              </p>
              <ul className="list-disc pl-6 mb-4 text-gray-600 space-y-2">
                <li>Authentication and session management (keeping you logged in)</li>
                <li>Security features (protecting against unauthorized access)</li>
                <li>Remembering your cookie preferences</li>
                <li>Form submission functionality</li>
              </ul>

              <h3 className="text-lg font-semibold text-gray-900 mt-6 mb-3">Functional Cookies</h3>
              <p className="text-gray-600 mb-4">
                These cookies enhance your experience by remembering your choices:
              </p>
              <ul className="list-disc pl-6 mb-4 text-gray-600 space-y-2">
                <li>Language preferences</li>
                <li>Theme settings (light/dark mode)</li>
                <li>Form builder preferences</li>
              </ul>

              <h3 className="text-lg font-semibold text-gray-900 mt-6 mb-3">Analytics Cookies</h3>
              <p className="text-gray-600 mb-4">
                We use analytics cookies to understand how visitors interact with our website. This helps us improve our services.
                We may use Google Analytics to collect anonymous usage data:
              </p>
              <ul className="list-disc pl-6 mb-4 text-gray-600 space-y-2">
                <li>Pages visited and time spent</li>
                <li>How you arrived at our site</li>
                <li>Features and tools used</li>
                <li>Technical information about your device and browser</li>
              </ul>
              <p className="text-gray-600 mb-4">
                This data is aggregated and anonymized. We do not use this information to identify individual users.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Cookies We Use</h2>
              <div className="overflow-x-auto">
                <table className="min-w-full border border-gray-200 rounded-lg">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 border-b">Cookie Name</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 border-b">Purpose</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 border-b">Duration</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-4 py-3 text-sm text-gray-600">auth_token</td>
                      <td className="px-4 py-3 text-sm text-gray-600">Authentication session</td>
                      <td className="px-4 py-3 text-sm text-gray-600">Session / 7 days</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm text-gray-600">cookie_consent</td>
                      <td className="px-4 py-3 text-sm text-gray-600">Remembers your cookie preferences</td>
                      <td className="px-4 py-3 text-sm text-gray-600">1 year</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm text-gray-600">i18next</td>
                      <td className="px-4 py-3 text-sm text-gray-600">Language preference</td>
                      <td className="px-4 py-3 text-sm text-gray-600">1 year</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm text-gray-600">_ga, _gid</td>
                      <td className="px-4 py-3 text-sm text-gray-600">Google Analytics tracking</td>
                      <td className="px-4 py-3 text-sm text-gray-600">2 years / 24 hours</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Third-Party Cookies</h2>
              <p className="text-gray-600 mb-4">
                We may use third-party services that set their own cookies:
              </p>
              <ul className="list-disc pl-6 mb-4 text-gray-600 space-y-2">
                <li><strong>Google Analytics</strong> - For website analytics and usage statistics</li>
                <li><strong>Cloudflare</strong> - For security and performance optimization</li>
              </ul>
              <p className="text-gray-600 mb-4">
                These third parties have their own privacy policies regarding how they use information collected through cookies.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Managing Your Cookie Preferences</h2>
              <p className="text-gray-600 mb-4">
                You have several options for managing cookies:
              </p>

              <h3 className="text-lg font-semibold text-gray-900 mt-6 mb-3">Browser Settings</h3>
              <p className="text-gray-600 mb-4">
                Most browsers allow you to control cookies through their settings. You can:
              </p>
              <ul className="list-disc pl-6 mb-4 text-gray-600 space-y-2">
                <li>Block all cookies</li>
                <li>Accept only first-party cookies</li>
                <li>Delete cookies when you close your browser</li>
                <li>Clear existing cookies</li>
              </ul>
              <p className="text-gray-600 mb-4">
                Please note that blocking essential cookies may affect the functionality of our website.
              </p>

              <h3 className="text-lg font-semibold text-gray-900 mt-6 mb-3">Our Cookie Consent Banner</h3>
              <p className="text-gray-600 mb-4">
                When you first visit our website, you'll see a cookie consent banner. You can accept or decline non-essential cookies.
                You can change your preferences at any time by clearing your cookies and revisiting our site.
              </p>

              <h3 className="text-lg font-semibold text-gray-900 mt-6 mb-3">Opt-Out Links</h3>
              <p className="text-gray-600 mb-4">
                To opt out of Google Analytics tracking across all websites, you can install the
                <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline ml-1">
                  Google Analytics Opt-out Browser Add-on
                </a>.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Updates to This Policy</h2>
              <p className="text-gray-600 mb-4">
                We may update this Cookie Policy from time to time to reflect changes in our practices or for legal, operational,
                or regulatory reasons. We will notify you of any material changes by posting the new policy on this page
                with an updated "Last updated" date.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Contact Us</h2>
              <p className="text-gray-600 mb-4">
                If you have any questions about our use of cookies, please contact us at:
              </p>
              <p className="text-gray-600">
                Email: <a href="mailto:team@xtform.com" className="text-blue-600 hover:underline">team@xtform.com</a>
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Related Policies</h2>
              <ul className="list-disc pl-6 text-gray-600 space-y-2">
                <li><Link to="/privacy" className="text-blue-600 hover:underline">Privacy Policy</Link></li>
                <li><Link to="/terms" className="text-blue-600 hover:underline">Terms of Service</Link></li>
              </ul>
            </section>
          </div>
        </article>
      </main>
    </PublicLayout>
  );
}
